// Ops → Automation → visual flow builder (React Flow). Instead of forms, an ECAP flow is drawn as a
// node graph: a Start node (greeting), Attribute nodes it collects, Branch nodes (conditional paths),
// Hook nodes (actions/transformers), and an End node (completion). Edges carry meaning:
//   attribute → attribute  = "depends on" (ask order),
//   branch → attribute     = that attribute belongs to the branch,
//   attribute → hook       = the hook fires after that attribute (ON_ATTRIBUTES trigger).
// Select a node to configure it in the inspector; Save serializes the graph back to the ECAP flow JSON
// (validation rules + localized messages on the loaded flow are preserved as-is). See src/pages/README.md.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap,
  useNodesState, useEdgesState, addEdge, Handle, Position, MarkerType, useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Button, Alert } from '../ui/primitives';
import {
  saveFlow, aiProfiles as aiProfilesApi, capabilities as capabilitiesApi,
  policies as policiesApi, connections as connectionsApi, domainTables as domainTablesApi,
  listCodeCapabilities,
} from '../services/ecapApi';

const COND_OPS = ['EQUALS', 'NOT_EQUALS', 'EXISTS', 'CONTAINS'];
const TRIGGERS = ['ON_COMPLETE', 'ON_ATTRIBUTES'];
const inp = 'w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm';

/* ---------- node visuals ---------- */
const box = (bg, brd) => ({
  background: bg, border: `1px solid ${brd}`, borderRadius: 10, padding: '8px 12px',
  minWidth: 150, fontSize: 12, boxShadow: '0 1px 2px rgba(0,0,0,.06)',
});
const H = (type, pos) => <Handle type={type} position={pos} style={{ width: 8, height: 8, background: '#94a3b8' }} />;

function StartNode({ data }) {
  return (<div style={box('#eef6ff', '#3b82f6')}><div className="font-semibold text-blue-700">▸ Start</div>
    <div className="text-slate-500 truncate" style={{ maxWidth: 180 }}>{data.name || data.id || 'flow'}</div>{H('source', Position.Right)}</div>);
}
function EndNode({ data }) {
  return (<div style={box('#effdf4', '#22c55e')}>{H('target', Position.Left)}<div className="font-semibold text-emerald-700">■ Complete</div>
    <div className="text-slate-500 truncate" style={{ maxWidth: 180 }}>{data.completionMessage || 'done'}</div></div>);
}
function AttributeNode({ data, selected }) {
  return (<div style={{ ...box('#fff', selected ? '#3b82f6' : '#e2e8f0'), outline: selected ? '2px solid #bfdbfe' : 'none' }}>
    {H('target', Position.Left)}
    <div className="font-semibold text-slate-800">{data.label || data.key || 'attribute'}</div>
    <div className="text-slate-400">{data.key}{data.required ? ' · required' : ''}{data.derived ? ' · AI' : ''}</div>
    {((data.policyRefs || []).length > 0 || (data.inlineRules || []).length > 0) && (
      <div className="text-emerald-600" style={{ fontSize: 10 }}>🛡 {(data.policyRefs || []).length + (data.inlineRules || []).length} validation</div>
    )}
    {H('source', Position.Right)}</div>);
}
const HOOK_BADGE = { code: '⚙ code', capability: '🔌 capability', decision: '🧠 AI decision', vision: '🖼 AI vision', readdoc: '📄 AI read doc', approval: '🙋 human approval', handoff: '🎧 live handoff' };
function HookNode({ data, selected }) {
  const t = actionType(data);
  const badge = HOOK_BADGE[t] || null;
  return (<div style={{ ...box('#fff7ed', selected ? '#f59e0b' : '#fed7aa') }}>
    {H('target', Position.Left)}<div className="font-semibold text-amber-700">⚡ {data.name || 'action'}</div>
    {badge && <div className={t === 'approval' ? 'text-amber-700 text-[11px]' : 'text-indigo-600 text-[11px]'}>{badge}{t === 'code' && data.codeCapabilityRef ? `: ${data.codeCapabilityRef}` : ''}</div>}
    <div className="text-slate-400">{data.trigger || 'ON_COMPLETE'}{data.componentTemplate ? ' · component' : ''}</div>{H('source', Position.Right)}</div>);
}
function BranchNode({ data, selected }) {
  const conds = data.conditions || [];
  const c0 = conds[0];
  const more = conds.length - 1;
  const summary = c0 && c0.attribute ? `${c0.attribute} ${c0.op} ${c0.value}${more > 0 ? ` +${more} more` : ''}` : 'when…';
  return (<div style={{ ...box('#faf5ff', selected ? '#a855f7' : '#e9d5ff') }}>
    {H('target', Position.Left)}<div className="font-semibold text-purple-700">⑃ {data.name || 'branch'}</div>
    <div className="text-slate-400 truncate" style={{ maxWidth: 180 }}>{summary}</div>{H('source', Position.Right)}</div>);
}
const nodeTypes = { start: StartNode, end: EndNode, attribute: AttributeNode, hook: HookNode, branch: BranchNode };

/* ---------- flow ⇄ graph ---------- */
// fieldKey → [policyRef] and fieldKey → [inline rule] from a flow's rules (shared + branch).
function indexRules(rules) {
  const policyByField = {};
  const inlineByField = {};
  // RT-03: an "always" policy rule is carried on the node as a bare policyRef string, so its own
  // severity/message/param/type were discarded and re-emitted as null on save (silently flipping a WARN
  // policy to a default BLOCK and dropping a custom message on externally-authored flows). Retain that
  // metadata in a side-map keyed by fieldKey→policyRef so graphToFlow can round-trip it. The editor still
  // reads/writes policyRefs as plain strings, so this is invisible to authoring.
  const policyMetaByField = {};
  (rules || []).forEach((r) => {
    if (!r.fieldKey) return;
    if (r.policyRef) {
      (policyByField[r.fieldKey] ||= []).push(r.policyRef);
      if (r.severity != null || r.message != null || r.param != null || r.type != null) {
        (policyMetaByField[r.fieldKey] ||= {})[r.policyRef] = {
          severity: r.severity ?? null, message: r.message ?? null, param: r.param ?? null, type: r.type ?? null };
      }
    } else (inlineByField[r.fieldKey] ||= []).push(r); // COMBINATION / inline REGEX etc. — preserved as-is
  });
  return { policyByField, inlineByField, policyMetaByField };
}

function flowToGraph(flow) {
  const f = flow || {};
  const nodes = [];
  const edges = [];
  nodes.push({ id: 'start', type: 'start', position: { x: 20, y: 40 }, data: { kind: 'start', id: f.id || '', name: f.name || '', greeting: f.greeting || '' } });
  const shared = indexRules(f.rules);
  // NOTE: a field's own `kind` (TEXT/FILE) must NOT clobber the node-type `kind:'attribute'` — the whole
  // canvas identifies attribute nodes by data.kind==='attribute'. Lift it to `attrKind` before spreading.
  const attrData = (fld, extra, idx) => { const { kind: attrKind, ...rest } = fld; return {
    kind: 'attribute', attrKind: attrKind || 'TEXT', ...rest,
    // BUG-25(hunt #7): remember the ORIGINAL key so a rename can still recover fields the inspector doesn't
    // edit (e.g. localizedPrompts) from origFieldByKey — indexing that fallback by the NEW key would miss.
    origKey: fld.key,
    policyRefs: idx.policyByField[fld.key] || [], inlineRules: idx.inlineByField[fld.key] || [],
    policyMeta: (idx.policyMetaByField || {})[fld.key] || {}, // RT-03: policyRef→{severity,message,param,type}
    ...extra }; };
  // One attribute NODE per key (a key may appear in several branches — e.g. departureFlight — so dedupe;
  // branch membership is carried by branch→attribute EDGES, which can point at the same node from many
  // branches). Without this the same key produced duplicate node ids (React Flow breaks, and the branch
  // multiselect listed it twice).
  const pushedAttr = new Set();
  // A hook→attribute edge means "ask this attribute only after this action succeeds" (dependsOnHooks) —
  // the visible connection a business user reads as "Quote → Confirm".
  const gateEdges = (fld) => (fld.dependsOnHooks || []).forEach((hk) => edges.push({ id: `hd:${hk}->${fld.key}`, source: `hook:${hk}`, target: `attr:${fld.key}` }));
  // dependsOn edges (attribute → attribute). Deduped by id so a field shared across branches (e.g. dropoff
  // in one-way AND round-trip) doesn't emit the same edge twice (which breaks React Flow). Applied to BOTH
  // base and branch fields — previously branch-field dependsOn wasn't drawn, so it looked unsaved and a
  // re-save then wiped it.
  const depAdded = new Set();
  const depEdges = (fld) => (fld.dependsOn || []).forEach((dep) => {
    const eid = `dep:${dep}->${fld.key}`;
    if (!depAdded.has(eid)) { depAdded.add(eid); edges.push({ id: eid, source: `attr:${dep}`, target: `attr:${fld.key}` }); }
  });
  (f.fields || []).forEach((fld, i) => {
    pushedAttr.add(`attr:${fld.key}`);
    nodes.push({ id: `attr:${fld.key}`, type: 'attribute', position: { x: 300, y: 20 + i * 90 }, data: attrData(fld, {}, shared) });
    depEdges(fld);
    gateEdges(fld);
  });
  (f.branches || []).forEach((b, bi) => {
    const bIdx = indexRules(b.rules);
    nodes.push({ id: `branch:${b.id}`, type: 'branch', position: { x: 620, y: 40 + bi * 160 }, data: { kind: 'branch', bid: b.id, name: b.name,
      // BUG-25(hunt #5): carry the FULL activation list. The engine AND-combines every condition
      // (BranchSelector.matches), so modelling only activation[0] silently dropped conditions 2..N on re-save.
      // RT-05: `?? ''` (not `|| ''`) so an EQUALS-0 or EQUALS-false condition survives the round-trip — `||`
      // coerced numeric zero / boolean false to '' and the branch stopped matching after a canvas Save.
      conditions: (b.activation || []).map((c) => ({ attribute: c.attribute || '', op: c.op || 'EQUALS', value: c.value ?? '' })) } });
    (b.fields || []).forEach((fld, i) => {
      const id = `attr:${fld.key}`;
      if (!pushedAttr.has(id)) {
        pushedAttr.add(id);
        // RT-04: a top-level rule (f.rules) whose fieldKey is a branch-ONLY field lands in the `shared` index
        // but the branch-only node was built from bIdx alone, so the rule matched no node and a Save dropped
        // it. Merge the shared index FOR THIS KEY onto the node so the rule rides it and round-trips (it
        // re-emits into this branch's rules — behaviorally equivalent since the field exists only here). This
        // block runs only for genuinely branch-only keys (shared keys are already pushed via f.fields), so no
        // dupe. Both source arrays exist for the SAME key, so shallow per-key merge is exact.
        const mIdx = {
          policyByField: { [fld.key]: [...(bIdx.policyByField[fld.key] || []), ...(shared.policyByField[fld.key] || [])] },
          inlineByField: { [fld.key]: [...(bIdx.inlineByField[fld.key] || []), ...(shared.inlineByField[fld.key] || [])] },
          policyMetaByField: { [fld.key]: { ...(shared.policyMetaByField || {})[fld.key], ...(bIdx.policyMetaByField || {})[fld.key] } },
        };
        nodes.push({ id, type: 'attribute', position: { x: 880, y: 40 + bi * 160 + i * 90 }, data: attrData(fld, {}, mIdx) });
      }
      edges.push({ id: `bf:${b.id}->${fld.key}`, source: `branch:${b.id}`, target: id });
      depEdges(fld); // branch-field dependsOn now drawn too (the bug: "ask dropoff after pickup" wasn't saving)
      gateEdges(fld);
    });
  });
  // Branch rules on a SHARED field (inline lookup OR policy reference) → tag onto that attribute's node
  // with the branch, so a validation that applies on only one path (e.g. served-city check on City→City)
  // is visible + editable. Branch-attribute rules are already on their node; those are skipped here.
  (f.branches || []).forEach((b) => {
    const bFieldKeys = new Set((b.fields || []).map((x) => x.key));
    (b.rules || []).forEach((r) => {
      if (bFieldKeys.has(r.fieldKey)) return; // branch-field rules already on that node
      const node = nodes.find((n) => n.id === `attr:${r.fieldKey}` && n.data.kind === 'attribute');
      if (node) node.data.inlineRules = [...(node.data.inlineRules || []), { ...r, branch: b.id }];
    });
  });
  (f.hooks || []).forEach((h, i) => {
    nodes.push({ id: `hook:${h.name}`, type: 'hook', position: { x: 620, y: 40 + ((f.branches || []).length) * 160 + i * 100 }, data: { kind: 'hook', ...h } });
    (h.triggerAttributes || []).forEach((k) => edges.push({ id: `ha:${k}->${h.name}`, source: `attr:${k}`, target: `hook:${h.name}` }));
  });
  nodes.push({ id: 'end', type: 'end', position: { x: 1160, y: 40 }, data: { kind: 'end', completionMessage: f.completionMessage || '', outOfScopeMessage: f.outOfScopeMessage || '', identityAttribute: f.identityAttribute || '', maxAskAttempts: f.maxAskAttempts ?? null } });

  // --- Sequence ("happy path") guide edges: the ORDER the engine asks things, so a reader sees what's next
  // and nothing is orphan. VISUAL ONLY — dashed, non-editable, and graphToFlow ignores 'seq:' ids. The solid
  // dependsOn/branch/trigger edges remain the editable structure. Derived from field order + branches + hooks.
  const seq = (a, b) => ({
    id: `seq:${a}>${b}`, source: a, target: b, selectable: false, deletable: false, focusable: false,
    style: { stroke: '#cbd5e1', strokeDasharray: '5 4' },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#cbd5e1', width: 14, height: 14 },
  });
  const has = (id) => nodes.some((n) => n.id === id);
  const baseKeys = (f.fields || []).map((x) => `attr:${x.key}`).filter(has);
  let prev = 'start';
  baseKeys.forEach((k) => { edges.push(seq(prev, k)); prev = k; });
  const branches = f.branches || [];
  // BUG-53: draw each branch's guide edge from ITS OWN condition attribute, not the first branch's. (Using
  // branches[0]'s attribute for all made every edge originate from the same node even when branches key off
  // different attributes — the canvas then contradicted the logic.) Fall back to the spine tail if that
  // attribute isn't a base-field node (e.g. it's set by a hook/derived).
  branches.forEach((b) => {
    if (!has(`branch:${b.id}`)) return;
    const bKey = b.activation?.[0]?.attribute;
    const from = (bKey && has(`attr:${bKey}`)) ? `attr:${bKey}` : prev;
    edges.push(seq(from, `branch:${b.id}`));
  });
  // Everything (the base spine tail + each branch) converges on the ON_COMPLETE actions, then Complete.
  const completeHooks = (f.hooks || []).filter((h) => (h.trigger || 'ON_COMPLETE') === 'ON_COMPLETE' && has(`hook:${h.name}`));
  const tails = [prev, ...branches.map((b) => `branch:${b.id}`).filter(has)];
  if (completeHooks.length) {
    tails.forEach((tl) => completeHooks.forEach((h) => edges.push(seq(tl, `hook:${h.name}`))));
    completeHooks.forEach((h) => edges.push(seq(`hook:${h.name}`, 'end')));
  } else {
    tails.forEach((tl) => edges.push(seq(tl, 'end')));
  }
  return { nodes, edges };
}

function graphToFlow(nodes, edges, original) {
  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const kind = (id) => byId[id]?.data?.kind;
  const start = nodes.find((n) => n.data.kind === 'start')?.data || {};
  const end = nodes.find((n) => n.data.kind === 'end')?.data || {};
  const attrNodes = nodes.filter((n) => n.data.kind === 'attribute');
  const branchOfAttr = {}; const depsOf = {}; const trigOf = {}; const gateOf = {};
  edges.forEach((e) => {
    if (String(e.id).startsWith('seq:')) return; // visual-only sequence guide edge — never persisted
    const s = kind(e.source), t = kind(e.target);
    if (s === 'attribute' && t === 'attribute') (depsOf[byId[e.target].data.key] ||= []).push(byId[e.source].data.key);
    if (s === 'branch' && t === 'attribute') (branchOfAttr[e.target] ||= []).push(byId[e.source].data.bid); // an attr can belong to several branches
    if (s === 'attribute' && t === 'hook') (trigOf[e.target] ||= []).push(byId[e.source].data.key);
    if (s === 'hook' && t === 'attribute') (gateOf[byId[e.target].data.key] ||= []).push(byId[e.source].data.name); // "ask after this action succeeds"
  });
  // BUG-11: the attribute inspector never surfaces prompts, examples, aliases, negativeExamples,
  // confidenceThreshold, localizedPrompts or offerOptional — so rebuilding a field from only the inspector's
  // keys wrote null/[] over all of them on EVERY save, flow-wide (a no-op Save destroyed extraction tuning).
  // Round-trip the ORIGINAL field's values for everything the canvas doesn't edit, then overlay only what it does.
  const origFieldByKey = {};
  (original?.fields || []).forEach((f) => { if (f && f.key) origFieldByKey[f.key] = f; });
  (original?.branches || []).forEach((b) => (b?.fields || []).forEach((f) => { if (f && f.key) origFieldByKey[f.key] = f; }));
  const toField = (n) => ({
    // Preserve anything the inspector still doesn't edit — index by the ORIGINAL key so a rename doesn't
    // strand it (BUG-25 hunt #7: this miss silently dropped localizedPrompts on a key rename).
    ...(origFieldByKey[n.data.origKey] || origFieldByKey[n.data.key] || {}),
    key: n.data.key, label: n.data.label || '', required: !!n.data.required, extractionHint: n.data.extractionHint || '',
    dependsOn: depsOf[n.data.key] || [], derived: !!n.data.derived, aiProfileRef: n.data.aiProfileRef || null,
    dependsOnHooks: gateOf[n.data.key] || [], kind: n.data.attrKind || 'TEXT',
    // The inspector now edits these directly (BUG-42/BUG-33 alignment) — serialize from node.data so edits win,
    // and so a key rename can't strand them on the stale origFieldByKey entry. node.data carries them from flowToGraph.
    prompts: n.data.prompts || null,
    examples: n.data.examples || [],
    aliases: n.data.aliases || [],
    negativeExamples: n.data.negativeExamples || [],
    offerOptional: !!n.data.offerOptional,
    // B1 typed attributes — edited in the inspector; node.data carries them from flowToGraph for loaded fields.
    dataType: n.data.dataType ?? null,
    options: n.data.options || [],
    // B2 dynamic choices — options sourced from an action's response.
    dynamicOptions: n.data.dynamicOptions ?? null,
    // B3 grouping — fields sharing a label are asked together.
    askGroup: n.data.askGroup ?? null,
    // Per-locale wording — carried in node.data (attrData spreads it), so serialize it from there rather than
    // relying solely on the key-based origFieldByKey fallback that a rename defeats (BUG-25 hunt #7).
    localizedPrompts: n.data.localizedPrompts ?? (origFieldByKey[n.data.origKey] || {}).localizedPrompts ?? null,
  });
  // An inline rule may carry a UI-only `branch` scope tag ("applies on: <branch>") so a validation on a
  // SHARED field can apply on just one path. Strip the tag before emitting the persisted rule.
  const stripBranch = (r) => { const { branch, ...rest } = r; return rest; };
  // RT-03: re-emit an "always" policy rule with the severity/message/param/type retained on the node
  // (n.data.policyMeta), falling back to null only when the source rule never carried them (e.g. a policy the
  // author just attached in the inspector, which has no metadata of its own — the engine resolves it).
  const policyRule = (key, ref, meta) => { const m = (meta || {})[ref] || {};
    return { fieldKey: key, type: m.type ?? null, param: m.param ?? null, message: m.message ?? null, severity: m.severity ?? null, policyRef: ref }; };
  const sharedAttrNodes = attrNodes.filter((n) => !(branchOfAttr[n.id] && branchOfAttr[n.id].length));
  const sharedFields = sharedAttrNodes.map(toField);

  // Top-level rules: shared attributes' policyRefs + their UN-scoped inline rules.
  const sharedRules = [];
  sharedAttrNodes.forEach((n) => {
    (n.data.policyRefs || []).forEach((ref) => sharedRules.push(policyRule(n.data.key, ref, n.data.policyMeta)));
    (n.data.inlineRules || []).filter((r) => !r.branch).forEach((r) => sharedRules.push({ ...stripBranch(r), fieldKey: n.data.key }));
  });
  // RT-02: fieldKey-less rules (e.g. off-contract COMBINATION/served-pairs rules authored via API/MCP) are
  // skipped by indexRules, so they ride no node and graphToFlow can't re-derive them — carry them straight
  // through from `original` so a canvas Save doesn't silently delete them. graphToFlow only emits
  // fieldKey-bearing rules from nodes, so appending fieldKey-less originals can't duplicate.
  (original?.rules || []).filter((r) => r && !r.fieldKey).forEach((r) => sharedRules.push(r));

  const origBranchById = Object.fromEntries((original?.branches || []).map((b) => [b.id, b]));
  const branches = nodes.filter((n) => n.data.kind === 'branch').map((bn) => {
    const bid = bn.data.bid;
    const bAttrs = attrNodes.filter((n) => branchOfAttr[n.id] && branchOfAttr[n.id].includes(bid));
    const branchRules = [];
    // Branch-attribute rules: their policyRefs + all their inline rules belong to this branch.
    bAttrs.forEach((n) => {
      (n.data.policyRefs || []).forEach((ref) => branchRules.push(policyRule(n.data.key, ref, n.data.policyMeta)));
      (n.data.inlineRules || []).forEach((r) => branchRules.push({ ...stripBranch(r), fieldKey: n.data.key }));
    });
    // SHARED-field rules (inline lookup OR policy reference) explicitly scoped to THIS branch — the
    // "applies on: <branch>" case. These now round-trip via the node, so no separate preservation.
    sharedAttrNodes.forEach((n) => {
      (n.data.inlineRules || []).filter((r) => r.branch === bid).forEach((r) => branchRules.push({ ...stripBranch(r), fieldKey: n.data.key }));
    });
    const orig = origBranchById[bid];
    // RT-02 (branch scope): carry through this branch's fieldKey-less rules that indexRules dropped.
    (orig?.rules || []).filter((r) => r && !r.fieldKey).forEach((r) => branchRules.push(r));
    return {
      // RT-BRANCH-ALLOWLIST: spread the ORIGINAL branch first so any branch-level property the canvas doesn't
      // model (the branch node carries only {bid,name,conditions}, not the whole branch) round-trips instead of
      // being wiped on Save. The modeled keys below (id/name/activation/fields/rules/hooks) always override.
      ...(orig || {}),
      id: bid, name: bn.data.name || bid,
      // BUG-25(hunt #5): emit EVERY condition (AND-combined by the engine), not just the first. Drop only
      // blank rows the author left empty.
      activation: (bn.data.conditions || []).filter((c) => c && c.attribute).map((c) => ({
        attribute: c.attribute, op: c.op || 'EQUALS', value: c.value ?? '' })), // RT-05: preserve 0 / false
      fields: bAttrs.map(toField),
      rules: branchRules,
      hooks: orig?.hooks || [], // branch-specific hooks preserved (not editable in the canvas yet)
    };
  });
  const hooks = nodes.filter((n) => n.data.kind === 'hook').map((n) => {
    // RT-HOOK-ALLOWLIST: carry through EVERY property flowToGraph put on the node before overlaying the
    // canvas-modeled keys. flowToGraph seeds a hook node with the WHOLE original hook (`{ kind:'hook', ...h }`),
    // so `carry` is a superset of the persisted hook plus any inspector edits. Rebuilding from only the
    // explicit key list below silently dropped any hook/action property the canvas doesn't model — a proven,
    // recurring class (RT-01 `headers` and `errorMessage` were each retro-added after being stripped, sending
    // runtime calls unauthenticated). The evolving ECAP hook model, and hooks authored via API/MCP/import,
    // carry fields the canvas has never modeled; spreading `carry` first lets them round-trip. Strip only the
    // node-type marker (`kind`); the modeled keys below still win. Using node.data (not an original-hook lookup
    // by name) also survives a rename — the node keeps the original props; only `name` changes.
    const { kind, ...carry } = n.data;
    return {
      ...carry,
      name: n.data.name, method: n.data.method || 'POST', url: n.data.url || '', bodyTemplate: n.data.bodyTemplate || '',
      responseMapping: n.data.responseMapping || {}, trigger: n.data.trigger || 'ON_COMPLETE',
      connectionRef: n.data.connectionRef || null, capabilityRef: n.data.capabilityRef || null,
      codeCapabilityRef: n.data.codeCapabilityRef || null,
      triggerAttributes: (n.data.trigger === 'ON_ATTRIBUTES') ? (n.data.triggerAttributes?.length ? n.data.triggerAttributes : (trigOf[n.id] || [])) : [],
      messageTemplate: n.data.messageTemplate || '', componentTemplate: n.data.componentTemplate || '',
      dependsOnHooks: n.data.dependsOnHooks || [], errorMessage: n.data.errorMessage || null,
      // RT-01: per-hook request headers (Authorization / X-Api-Key / Content-Type) MUST round-trip. flowToGraph
      // carries them onto node.data via `...h` (line ~150); without re-emitting them here a canvas Save rebuilt
      // the hook from an explicit key list that omitted headers, silently stripping them — the runtime HTTP call
      // then went out unauthenticated. Editable via the REST-action inspector (headers editor).
      headers: n.data.headers || {},
    };
  });
  return {
    // RT-FLOW-TOPLEVEL-ALLOWLIST: spread the ORIGINAL flow first so any top-level flow property the canvas
    // doesn't model round-trips instead of being wiped on Save (saveFlow is a full-replace PUT). This matches
    // the form editor, which PUTs the whole loaded flow back verbatim (OpsFlowEditor deep-clones `flow`), so
    // it's the proven-safe baseline — not a new risk. Every canvas-modeled key below still overrides, so the
    // authored graph always wins; only un-modeled config (e.g. a future top-level flag) is preserved.
    ...(original || {}),
    id: start.id, name: start.name, greeting: start.greeting,
    completionMessage: end.completionMessage, outOfScopeMessage: end.outOfScopeMessage,
    identityAttribute: end.identityAttribute || null,
    maxAskAttempts: end.maxAskAttempts != null && end.maxAskAttempts !== '' ? Math.max(1, parseInt(end.maxAskAttempts, 10) || 1) : null,
    fields: sharedFields, rules: sharedRules, hooks, branches,
    localizedMessages: original?.localizedMessages || {},
    llmModel: original?.llmModel ?? null,
  };
}

// Exported for unit testing the pure flow⇄graph serialization (e.g. the BUG-11 round-trip regression).
export { flowToGraph, graphToFlow };

// BUG-52: node search. Above ~40 nodes the canvas has no way to find one by name, so an author
// scans visually. Match key/name/label/id case-insensitively (substring); cap the result list so a
// broad query ("a") doesn't render an unbounded dropdown. Pure so it can be unit-tested without React Flow.
export function matchNodes(nodes, query, limit = 8) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return [];
  return (nodes || []).filter((n) => {
    const d = n.data || {};
    return [d.key, d.name, d.label, n.id].filter(Boolean).join(' ').toLowerCase().includes(q);
  }).slice(0, Math.max(0, limit));
}

// BUG-52: incident-edge highlight. "Selecting a node highlights nothing" — you couldn't see what a node
// connects to in a dense graph. When a node is selected, emphasize the edges touching it (blue, thicker,
// animated) and dim the rest. Returns a DISPLAY-ONLY decorated copy — the underlying edge state that
// graphToFlow serializes on save is never mutated, so styling never leaks into the persisted flow.
export function decorateEdges(edges, selId) {
  if (!selId) return edges;
  return (edges || []).map((e) => {
    const incident = e.source === selId || e.target === selId;
    return incident
      ? { ...e, animated: true, zIndex: 10, style: { ...(e.style || {}), stroke: '#2563eb', strokeWidth: 2.5, opacity: 1 } }
      : { ...e, style: { ...(e.style || {}), opacity: 0.2 } };
  });
}

/* ---------- editor ---------- */
// Tidy: a self-contained layered layout (no layout lib). Columns by role — Start | base attributes |
// branches | branch fields | actions | End — so the graph reads left-to-right along the conversation.
// Within a column, keep the current vertical order (stable, non-destructive; you can still drag after).
function tidyLayout(nodes, edges) {
  const branchFieldIds = new Set(edges.filter((e) => String(e.id).startsWith('bf:')).map((e) => e.target));
  const colOf = (n) => {
    switch (n.data.kind) {
      case 'start': return 0;
      case 'attribute': return branchFieldIds.has(n.id) ? 3 : 1;
      case 'branch': return 2;
      case 'hook': return 4;
      case 'end': return 5;
      default: return 1;
    }
  };
  const cols = {};
  nodes.forEach((n) => { const c = colOf(n); (cols[c] ||= []).push(n); });
  const COL_W = 300; const ROW_H = 108; const TOP = 30;
  const pos = {};
  Object.keys(cols).forEach((c) => {
    cols[c].slice().sort((a, b) => (a.position?.y || 0) - (b.position?.y || 0))
      .forEach((n, i) => { pos[n.id] = { x: 20 + Number(c) * COL_W, y: TOP + i * ROW_H }; });
  });
  return nodes.map((n) => ({ ...n, position: pos[n.id] || n.position }));
}

function Canvas({ flow, authSession, onSaved, onCancel }) {
  const { fitView } = useReactFlow();
  const init = useMemo(() => flowToGraph(flow), [flow]);
  const [nodes, setNodes, onNodesChange] = useNodesState(init.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(init.edges);
  const [selId, setSelId] = useState(null);
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [libs, setLibs] = useState({ aiProfiles: [], capabilities: [], policies: [], connections: [], domainTables: [], codeCapabilities: [] });
  let seq = useMemo(() => ({ n: 1 }), []);

  useEffect(() => {
    let alive = true;
    Promise.all([
      aiProfilesApi.list(authSession).catch(() => []),
      capabilitiesApi.list(authSession).catch(() => []),
      policiesApi.list(authSession).catch(() => []),
      connectionsApi.list(authSession).catch(() => []),
      domainTablesApi.list(authSession).catch(() => []),
      listCodeCapabilities(authSession).catch(() => []),
    ]).then(([ap, cap, pol, conn, dt, cc]) => {
      if (alive) setLibs({ aiProfiles: ap || [], capabilities: cap || [], policies: pol || [], connections: conn || [], domainTables: dt || [], codeCapabilities: cc || [] });
    });
    return () => { alive = false; };
  }, [authSession]);

  const onConnect = useCallback((c) => setEdges((es) => addEdge({ ...c, id: `e${Date.now()}` }, es)), [setEdges]);

  // BUG-52: incident-edge highlight + node search/jump-to. `displayEdges` is a decorated copy for the
  // canvas only (never persisted); `searchMatches` drives a jump box so a node can be found by name in a
  // large graph. jumpTo selects the node and centers the viewport on it (fitView scoped to that node).
  const displayEdges = useMemo(() => decorateEdges(edges, selId), [edges, selId]);
  const searchMatches = useMemo(() => matchNodes(nodes, query), [nodes, query]);
  const nodeLabel = (n) => n.data.key || n.data.name || n.data.label || n.id;
  const kindTag = (k) => (k === 'attribute' ? 'attr' : k === 'hook' ? 'action' : k === 'branch' ? 'branch' : k);
  const jumpTo = useCallback((id) => {
    setSelId(id);
    setQuery('');
    setTimeout(() => fitView({ nodes: [{ id }], padding: 0.6, duration: 400, maxZoom: 1.5 }), 30);
  }, [fitView]);

  // Explicitly set which attributes a branch collects (manages branch→attribute edges) — the dropdown
  // alternative to dragging handles. Membership is purely edge-based, so rebuilding this branch's
  // attribute edges is all that's needed; both selecting and deselecting take effect on save.
  const setBranchAttrs = useCallback((branchNodeId, attrIds) => {
    const attrIdSet = new Set(nodes.filter((n) => n.data.kind === 'attribute').map((n) => n.id));
    setEdges((es) => {
      const kept = es.filter((e) => !(e.source === branchNodeId && attrIdSet.has(e.target)));
      const added = attrIds.map((tid) => ({ id: `bf:${branchNodeId}->${tid}`, source: branchNodeId, target: tid }));
      return [...kept, ...added];
    });
  }, [nodes, setEdges]);

  // "Only ask this attribute after these actions succeed" — manages hook→attribute (gate) edges.
  const setAttrGates = useCallback((attrNodeId, hookNodeIds) => {
    const hookIdSet = new Set(nodes.filter((n) => n.data.kind === 'hook').map((n) => n.id));
    setEdges((es) => {
      const kept = es.filter((e) => !(e.target === attrNodeId && hookIdSet.has(e.source)));
      const added = hookNodeIds.map((hid) => ({ id: `hd:${hid}->${attrNodeId}`, source: hid, target: attrNodeId }));
      return [...kept, ...added];
    });
  }, [nodes, setEdges]);

  // "Only ask this attribute after these OTHER attributes are provided" — manages attribute→attribute
  // (depends-on) edges. The dropdown alternative to dragging, for ask ordering.
  const setAttrDeps = useCallback((attrNodeId, depNodeIds) => {
    const attrIdSet = new Set(nodes.filter((n) => n.data.kind === 'attribute').map((n) => n.id));
    setEdges((es) => {
      const kept = es.filter((e) => !(e.target === attrNodeId && e.source !== attrNodeId && attrIdSet.has(e.source)));
      const added = depNodeIds.filter((sid) => sid !== attrNodeId).map((sid) => ({ id: `dep:${sid}->${attrNodeId}`, source: sid, target: attrNodeId }));
      return [...kept, ...added];
    });
  }, [nodes, setEdges]);
  const sel = nodes.find((n) => n.id === selId) || null;

  const patch = (p) => setNodes((ns) => ns.map((n) => (n.id === selId ? { ...n, data: { ...n.data, ...p } } : n)));

  const add = (kind) => {
    const id = kind === 'attribute' ? `attr:new${seq.n}` : kind === 'hook' ? `hook:action${seq.n}` : `branch:branch${seq.n}`;
    seq.n += 1;
    const data = kind === 'attribute' ? { kind, key: `new${seq.n}`, label: '', required: true, extractionHint: '', dependsOn: [], derived: false }
      : kind === 'hook' ? { kind, name: `action${seq.n}`, trigger: 'ON_COMPLETE', method: 'POST', url: '', bodyTemplate: '', responseMapping: {}, messageTemplate: '', componentTemplate: '' }
        : { kind, bid: `branch${seq.n}`, name: `Branch ${seq.n}`, conditions: [{ attribute: '', op: 'EQUALS', value: '' }] };
    setNodes((ns) => [...ns, { id, type: kind, position: { x: 320 + Math.random() * 120, y: 60 + Math.random() * 260 }, data }]);
  };
  const delSel = () => { if (!selId || sel?.data.kind === 'start' || sel?.data.kind === 'end') return; setNodes((ns) => ns.filter((n) => n.id !== selId)); setEdges((es) => es.filter((e) => e.source !== selId && e.target !== selId)); setSelId(null); };

  const save = async () => {
    const built = graphToFlow(nodes, edges, flow);
    if (!built.id?.trim()) { setError('Set a flow id on the Start node.'); return; }
    setSaving(true); setError(null);
    try { const saved = await saveFlow(built, authSession); onSaved?.(saved); }
    catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  // Guardrail (computed, non-destructive): an ON_ATTRIBUTES action whose trigger attributes span
  // mutually-exclusive branches can NEVER fire — only one branch runs per session, so the AND is never
  // satisfied. Also flag attribute links on an ON_COMPLETE action (they're ignored). Warn, don't block.
  const hookWarnings = useMemo(() => {
    const branchesOfAttr = {};
    edges.forEach((e) => { if (String(e.id).startsWith('bf:')) (branchesOfAttr[e.target] ||= new Set()).add(e.source); });
    const attrIds = new Set(nodes.filter((n) => n.data.kind === 'attribute').map((n) => n.id));
    const warn = {};
    nodes.filter((n) => n.data.kind === 'hook').forEach((h) => {
      const trig = edges.filter((e) => e.target === h.id && attrIds.has(e.source)).map((e) => e.source);
      if (!trig.length) return;
      if ((h.data.trigger || 'ON_COMPLETE') !== 'ON_ATTRIBUTES') {
        warn[h.id] = 'This action is ON_COMPLETE — it runs at the end of whichever branch is active, so the attribute links here are ignored. Switch to ON_ATTRIBUTES (with attributes from ONE branch) only if you want it to fire mid-flow.';
        return;
      }
      const nonBase = trig.filter((a) => branchesOfAttr[a] && branchesOfAttr[a].size);
      if (!nonBase.length) return; // all base attributes — a valid AND
      let common = null;
      nonBase.forEach((a) => { const bs = branchesOfAttr[a]; common = common ? new Set([...common].filter((x) => bs.has(x))) : new Set(bs); });
      if (!common || common.size === 0) {
        warn[h.id] = 'This action links attributes from DIFFERENT branches (mutually exclusive) — only one branch runs per session, so it can never fire. Keep all trigger attributes in ONE branch, or use ON_COMPLETE to run at the end of the active branch.';
      }
    });
    return warn;
  }, [nodes, edges]);
  const warnCount = Object.keys(hookWarnings).length;

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden flex flex-col h-full">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 flex-wrap shrink-0">
        <span className="font-semibold text-slate-800 mr-2">Flow builder</span>
        <Button size="sm" variant="outline" onClick={() => add('attribute')}>+ Attribute</Button>
        <Button size="sm" variant="outline" onClick={() => add('branch')}>+ Branch</Button>
        <Button size="sm" variant="outline" onClick={() => add('hook')}>+ Action</Button>
        <Button size="sm" variant="ghost" title="Re-lay the graph into a clean left-to-right pipeline"
          onClick={() => { setNodes((ns) => tidyLayout(ns, edges)); setTimeout(() => fitView({ padding: 0.2, duration: 300 }), 60); }}>✨ Tidy</Button>
        <div className="relative">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && searchMatches[0]) jumpTo(searchMatches[0].id); if (e.key === 'Escape') setQuery(''); }}
            placeholder="🔍 Find node…"
            aria-label="Find node"
            className="h-8 w-40 rounded-md border border-slate-200 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
          {searchMatches.length > 0 && (
            <div className="absolute left-0 z-20 mt-1 w-56 max-h-60 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg text-xs">
              {searchMatches.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); jumpTo(n.id); }}
                  className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left hover:bg-slate-50"
                >
                  <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-500">{kindTag(n.data.kind)}</span>
                  <span className="truncate text-slate-700">{nodeLabel(n)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex-1" />
        <Button size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save flow'}</Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Close</Button>
      </div>
      {error && <div className="px-4 pt-3 shrink-0"><Alert tone="danger">{error}</Alert></div>}
      {warnCount > 0 && (
        <div className="px-4 pt-3 shrink-0">
          <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-xs px-3 py-2">
            ⚠ {warnCount} action{warnCount > 1 ? 's' : ''} may never fire (attributes linked across branches, or links on an ON_COMPLETE action). Select the action to see why &amp; how to fix.
          </div>
        </div>
      )}
      <div className="grid flex-1 min-h-0" style={{ gridTemplateColumns: '1fr 340px' }}>
        <div style={{ height: '100%' }}>
          <ReactFlow
            nodes={nodes} edges={displayEdges} nodeTypes={nodeTypes}
            onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect}
            onNodeClick={(_, n) => setSelId(n.id)} onPaneClick={() => setSelId(null)}
            fitView minZoom={0.1} fitViewOptions={{ padding: 0.15 }} proOptions={{ hideAttribution: true }}
          >
            <Background gap={16} color="#eef2f7" />
            <MiniMap pannable zoomable />
            <Controls />
          </ReactFlow>
        </div>
        <div className="border-l border-slate-200 p-3 overflow-auto text-sm">
          {!sel && <div className="text-slate-400">Select a node to configure it. Drag between handles to link nodes (attribute→attribute = depends-on, branch→attribute = branch member, attribute→action = trigger).</div>}
          {sel && <Inspector node={sel} libs={libs} warning={hookWarnings[sel.id]}
            attrKeys={nodes.filter((n) => n.data.kind === 'attribute').map((n) => n.data.key)}
            hookNames={nodes.filter((n) => n.data.kind === 'hook').map((n) => n.data.name)}
            attrNodes={nodes.filter((n) => n.data.kind === 'attribute').map((n) => ({ id: n.id, name: n.data.key || n.id }))}
            branchAttrIds={sel.data.kind === 'branch'
              ? edges.filter((e) => e.source === sel.id && nodes.some((n) => n.id === e.target && n.data.kind === 'attribute')).map((e) => e.target)
              : []}
            onSetBranchAttrs={(ids) => setBranchAttrs(sel.id, ids)}
            gateHookNodes={nodes.filter((n) => n.data.kind === 'hook').map((n) => ({ id: n.id, name: n.data.name || n.id }))}
            attrGateIds={sel.data.kind === 'attribute'
              ? edges.filter((e) => e.target === sel.id && nodes.some((n) => n.id === e.source && n.data.kind === 'hook')).map((e) => e.source)
              : []}
            onSetAttrGates={(ids) => setAttrGates(sel.id, ids)}
            attrDepIds={sel.data.kind === 'attribute'
              ? edges.filter((e) => e.target === sel.id && e.source !== sel.id && nodes.some((n) => n.id === e.source && n.data.kind === 'attribute')).map((e) => e.source)
              : []}
            onSetAttrDeps={(ids) => setAttrDeps(sel.id, ids)}
            branches={nodes.filter((n) => n.data.kind === 'branch').map((n) => ({ id: n.data.bid, name: n.data.name || n.data.bid }))}
            onChange={patch} onDelete={delSel} />}
        </div>
      </div>
    </div>
  );
}

// A checkbox list for selecting several library refs (e.g. validation policies on an attribute).
function MultiRef({ items, selected, onChange, empty }) {
  const sel = selected || [];
  const toggle = (id) => onChange(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  if (!items || items.length === 0) return <div className="text-[11px] text-slate-400">{empty || 'None available.'}</div>;
  return (
    <div className="rounded-md border border-slate-200 max-h-36 overflow-auto">
      {items.map((it) => (
        <label key={it.id} className="flex items-center gap-2 px-2 py-1 text-sm hover:bg-slate-50 cursor-pointer">
          <input type="checkbox" checked={sel.includes(it.id)} onChange={() => toggle(it.id)} />
          <span className="truncate">{it.name ? `${it.name}` : it.id}</span>
        </label>
      ))}
    </div>
  );
}

// Attach reusable validation policies to an attribute, each scoped "Always" or "Only on <branch>".
// Always-policies live in policyRefs[]; branch-scoped ones live in inlineRules as {policyRef, branch}
// (which the flow's rules routing then places on that branch). Lookups/other inline rules are untouched.
function PolicyRuleEditor({ policyRefs, inlineRules, policies, branches, onChange }) {
  const rules = inlineRules || [];
  const nonPolicyInline = rules.filter((r) => !r.policyRef);
  const items = [
    ...(policyRefs || []).map((ref) => ({ ref, branch: '' })),
    ...rules.filter((r) => r.policyRef).map((r) => ({ ref: r.policyRef, branch: r.branch || '' })),
  ];
  const [pick, setPick] = useState('');
  const [scope, setScope] = useState('');
  const nameOf = (id) => (policies || []).find((p) => p.id === id)?.name || id;
  const emit = (next) => {
    const always = [...new Set(next.filter((i) => !i.branch).map((i) => i.ref))];
    const scoped = next.filter((i) => i.branch).map((i) => ({ fieldKey: null, type: null, param: null, message: null, severity: 'BLOCK', policyRef: i.ref, branch: i.branch }));
    onChange({ policyRefs: always, inlineRules: [...nonPolicyInline, ...scoped] });
  };
  const add = () => { if (!pick || items.some((i) => i.ref === pick && (i.branch || '') === scope)) { setPick(''); return; } emit([...items, { ref: pick, branch: scope }]); setPick(''); };
  return (
    <div className="rounded-md border border-slate-200 p-2">
      {items.length === 0 && <div className="text-[11px] text-slate-400 mb-1">No policies attached.</div>}
      {items.map((it, idx) => (
        <div key={idx} className="flex items-center gap-1.5 text-xs mb-1">
          <span className="flex-1 truncate text-slate-700" title={it.ref}>{nameOf(it.ref)}</span>
          {(branches || []).length > 0 && (
            <select className="rounded border border-slate-200 px-1 py-0.5 text-[11px]" value={it.branch} onChange={(e) => emit(items.map((x, j) => (j === idx ? { ...x, branch: e.target.value } : x)))} title="Applies on">
              <option value="">always</option>
              {branches.map((b) => <option key={b.id} value={b.id}>on {b.name}</option>)}
            </select>
          )}
          <button className="text-rose-500 hover:text-rose-600" onClick={() => emit(items.filter((_, j) => j !== idx))} aria-label="Remove">✕</button>
        </div>
      ))}
      {(policies || []).length === 0
        ? <div className="text-[11px] text-slate-400">No policies in the Library yet.</div>
        : (
          <div className="mt-1.5 space-y-1.5 border-t border-slate-100 pt-2">
            <select className={inp} value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">Add a policy…</option>
              {policies.map((p) => <option key={p.id} value={p.id}>{p.name || p.id}</option>)}
            </select>
            {(branches || []).length > 0 && (
              <select className={inp} value={scope} onChange={(e) => setScope(e.target.value)}>
                <option value="">Applies always</option>
                {branches.map((b) => <option key={b.id} value={b.id}>Only on {b.name}</option>)}
              </select>
            )}
            <button className="w-full text-xs px-2 py-1.5 rounded bg-blue-600 text-white disabled:opacity-40" disabled={!pick} onClick={add}>+ Add policy</button>
          </div>
        )}
    </div>
  );
}

// Author domain-table lookups on an attribute WITHOUT typing a param string. Pick a table (+ column for
// value-in-column, or the table's composite key for row-match) from dropdowns → builds the inline rule.
// Non-lookup inline rules on the field (e.g. inline REGEX) are preserved untouched.
function DomainRuleEditor({ inlineRules, tables, branches, onChange }) {
  const rules = inlineRules || [];
  const LOOKUP_TYPES = ['LOOKUP_EXISTS', 'COMBINATION'];
  const isLookup = (r) => LOOKUP_TYPES.includes(r.type);
  const others = rules.filter((r) => !isLookup(r));
  const lookups = rules.filter(isLookup);
  const [tableId, setTableId] = useState('');
  const [kind, setKind] = useState('LOOKUP_EXISTS');
  const [column, setColumn] = useState('');
  const [scope, setScope] = useState(''); // '' = always; else a branch id
  const table = (tables || []).find((t) => t.id === tableId);
  const columns = (table?.columns || []).map((c) => c.name || c);
  const add = () => {
    if (!tableId) return;
    const param = kind === 'LOOKUP_EXISTS' ? `${tableId}.${column || columns[0] || 'name'}` : tableId;
    const msg = kind === 'LOOKUP_EXISTS'
      ? `That value isn't in ${tableId}.` : `That combination isn't a valid ${tableId} row.`;
    const rule = { fieldKey: null, type: kind, param, message: msg, severity: 'BLOCK', policyRef: null };
    if (scope) rule.branch = scope; // applies on just this branch/path
    onChange([...others, ...lookups, rule]);
    setColumn('');
  };
  const removeAt = (i) => onChange([...others, ...lookups.filter((_, j) => j !== i)]);
  const setScopeAt = (i, branch) => onChange([...others, ...lookups.map((r, j) => (j === i ? { ...r, branch: branch || undefined } : r))]);
  return (
    <div className="rounded-md border border-slate-200 p-2">
      {lookups.length === 0 && <div className="text-[11px] text-slate-400 mb-1">No lookups yet.</div>}
      {lookups.map((r, i) => (
        <div key={i} className="flex items-center gap-1.5 text-xs mb-1">
          <span className={`px-1.5 rounded ${r.type === 'COMBINATION' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'}`}>{r.type === 'COMBINATION' ? 'row match' : 'in column'}</span>
          <span className="font-mono text-slate-700 flex-1 truncate" title={r.param}>{r.param}</span>
          {(branches || []).length > 0 && (
            <select className="rounded border border-slate-200 px-1 py-0.5 text-[11px]" value={r.branch || ''} onChange={(e) => setScopeAt(i, e.target.value)} title="Applies on">
              <option value="">always</option>
              {branches.map((b) => <option key={b.id} value={b.id}>on {b.name}</option>)}
            </select>
          )}
          <button className="text-rose-500 hover:text-rose-600" onClick={() => removeAt(i)} aria-label="Remove">✕</button>
        </div>
      ))}
      {(tables || []).length === 0
        ? <div className="text-[11px] text-slate-400">No domain tables in the Library yet.</div>
        : (
          <div className="mt-1.5 space-y-1.5 border-t border-slate-100 pt-2">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">Add a lookup</div>
            <select className={inp} value={tableId} onChange={(e) => { setTableId(e.target.value); setColumn(''); }}>
              <option value="">Reference table…</option>
              {tables.map((t) => <option key={t.id} value={t.id}>{t.name || t.id}</option>)}
            </select>
            <select className={inp} value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="LOOKUP_EXISTS">value must be in a column</option>
              <option value="COMBINATION">attributes must match a row (composite key)</option>
            </select>
            {kind === 'LOOKUP_EXISTS' && (
              <select className={inp} value={column} onChange={(e) => setColumn(e.target.value)} disabled={!table}>
                <option value="">{table ? (columns[0] ? `column — default ${columns[0]}` : 'column…') : 'pick a table first'}</option>
                {columns.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            {kind === 'COMBINATION' && table && (
              <div className="text-[11px] text-slate-400">Matches on key: <b>{(table.keyColumns || []).join(', ') || '(set key columns on the table)'}</b></div>
            )}
            {(branches || []).length > 0 && (
              <select className={inp} value={scope} onChange={(e) => setScope(e.target.value)} title="Which path this validation applies on">
                <option value="">Applies always</option>
                {branches.map((b) => <option key={b.id} value={b.id}>Only on {b.name}</option>)}
              </select>
            )}
            <button className="w-full text-xs px-2 py-1.5 rounded bg-blue-600 text-white disabled:opacity-40" disabled={!tableId} onClick={add}>+ Add lookup</button>
          </div>
        )}
    </div>
  );
}

// Defined at MODULE level (not inside Inspector) so their identity is stable across renders — otherwise
// every keystroke remounts the inputs and focus is lost (you could only type one character at a time).
function Row({ label, children }) {
  return <label className="block mb-2"><span className="block text-xs text-slate-500 mb-1">{label}</span>{children}</label>;
}
// A library-ref <select> that still shows an unknown/typed id (so stale refs aren't silently dropped).
function RefSelect({ value, items, onPick }) {
  const ids = (items || []).map((it) => it.id);
  const known = !value || ids.includes(value);
  return (<select className={inp} value={value || ''} onChange={(e) => onPick(e.target.value || null)}>
    <option value="">— none —</option>
    {(items || []).map((it) => <option key={it.id} value={it.id}>{it.name ? `${it.name} (${it.id})` : it.id}</option>)}
    {!known && <option value={value}>{value} (not in library)</option>}
  </select>);
}

// --- Action "type" derivation + URL/mapping helpers, so AI-decision and human-approval actions are
// configured with friendly fields instead of raw ai://decide?options=… / human://approve?when=… URLs.
function actionType(d) {
  if (d.codeCapabilityRef) return 'code';
  if (d.capabilityRef) return 'capability';
  const u = d.url || '';
  if (u.startsWith('ai://decide')) return 'decision';
  if (u.startsWith('ai://vision')) return 'vision';
  if (u.startsWith('ai://readdoc')) return 'readdoc';
  if (u.startsWith('human://handoff')) return 'handoff';
  if (u.startsWith('human://')) return 'approval';
  return 'rest';
}
function qParam(url, key) { const m = new RegExp('[?&]' + key + '=([^&]*)').exec(url || ''); return m ? decodeURIComponent(m[1]) : ''; }
function parseOpts(url) { return qParam(url, 'options').split(/[|,]/).map((s) => s.trim()).filter(Boolean); }
function parseWhen(url) { const w = qParam(url, 'when'); const i = w.indexOf(':'); return i > 0 ? { attr: w.slice(0, i), val: w.slice(i + 1) } : { attr: '', val: '' }; }
function decideUrl(opts) { return 'ai://decide?options=' + (opts.length ? opts : ['approve', 'deny', 'review']).join('|'); }
function approveUrl(opts, when) { let u = 'human://approve?options=' + (opts.length ? opts : ['approve', 'deny']).join('|'); if (when && when.attr && when.val) u += '&when=' + encodeURIComponent(when.attr.trim() + ':' + when.val.trim()); return u; }
function handoffUrl(when) { let u = 'human://handoff'; if (when && when.attr && when.val) u += '?when=' + encodeURIComponent(when.attr.trim() + ':' + when.val.trim()); return u; }
// responseMapping for decision/approval: {attr: 'decision'} or {attr: 'reason'}.
function decisionAttr(d) { const e = Object.entries(d.responseMapping || {}).find(([, v]) => v !== 'reason'); return e ? e[0] : ''; }
function reasonAttr(d) { const e = Object.entries(d.responseMapping || {}).find(([, v]) => v === 'reason'); return e ? e[0] : ''; }
function decReaMapping(dec, rea) { const m = {}; if (dec) m[dec] = 'decision'; if (rea) m[rea] = 'reason'; return m; }
// AI vision writes its answer to a single output attribute (the mapping value is unused by the executor).
function visionMapping(attr) { return attr ? { [attr]: 'result' } : {}; }

export function Inspector({ node, attrKeys, hookNames, libs, warning, onChange, onDelete, attrNodes, branchAttrIds, onSetBranchAttrs, gateHookNodes, attrGateIds, onSetAttrGates, attrDepIds, onSetAttrDeps, branches }) {
  const d = node.data;
  if (d.kind === 'start') return (<div>
    <Head>Start / basics</Head>
    <Row label="Flow id"><input className={inp} value={d.id} onChange={(e) => onChange({ id: e.target.value.trim() })} placeholder="cab-booking" /></Row>
    <Row label="Name"><input className={inp} value={d.name} onChange={(e) => onChange({ name: e.target.value })} /></Row>
    <Row label="Greeting"><textarea className={inp} rows={3} value={d.greeting} onChange={(e) => onChange({ greeting: e.target.value })} /></Row>
  </div>);
  if (d.kind === 'end') return (<div>
    <Head>Completion</Head>
    <Row label="Completion message"><textarea className={inp} rows={3} value={d.completionMessage} onChange={(e) => onChange({ completionMessage: e.target.value })} /></Row>
    <Row label="Out-of-scope reply"><textarea className={inp} rows={2} value={d.outOfScopeMessage} onChange={(e) => onChange({ outOfScopeMessage: e.target.value })} /></Row>
    <Row label="Identity attribute"><input className={inp} value={d.identityAttribute || ''} onChange={(e) => onChange({ identityAttribute: e.target.value.trim() })} placeholder="email" /></Row>
    <Row label="Max re-asks before giving up"><input className={inp} type="number" min={1} value={d.maxAskAttempts ?? ''} onChange={(e) => onChange({ maxAskAttempts: e.target.value === '' ? null : Math.max(1, parseInt(e.target.value, 10) || 1) })} placeholder="default 3" /></Row>
  </div>);
  if (d.kind === 'attribute') {
    const p = d.prompts || {};
    // prompts is a nested object; merge so editing one sub-field keeps the others. '' clears back to null.
    const setPrompt = (k, v) => onChange({ prompts: { ...p, [k]: (v === '' || v == null) ? null : v } });
    const listVal = (a) => (a || []).join(', ');
    const setList = (k, s) => onChange({ [k]: s.split(',').map((x) => x.trim()).filter(Boolean) });
    const gated = d.attrKind === 'FILE' || d.attrKind === 'COMPONENT'; // never AI-extracted → hide extraction/confidence
    return (<div>
    <Head onDelete={onDelete}>Attribute</Head>
    <Row label="Key"><input className={inp} value={d.key} onChange={(e) => onChange({ key: e.target.value.trim() })} /></Row>
    <Row label="Label (shown to user)"><input className={inp} value={d.label || ''} onChange={(e) => onChange({ label: e.target.value })} /></Row>
    <Row label="Type"><select className={inp} value={d.attrKind || 'TEXT'} onChange={(e) => onChange({ attrKind: e.target.value })}>
      <option value="TEXT">Text (AI-extracted from chat)</option>
      <option value="FILE">File upload (image / PDF / CSV)</option>
      <option value="COMPONENT">Component result (e.g. Pay button)</option>
    </select></Row>
    {/* B1 typed attributes: the ANSWER's data type. It drives the extraction schema, normalization and the
        implicit validation — "a number", "yes/no", "one of these" need no regex or Library policy. */}
    {(d.attrKind || 'TEXT') === 'TEXT' && <>
    <Row label="Answer type"><select className={inp} value={d.dataType || 'text'} onChange={(e) => onChange({ dataType: e.target.value === 'text' ? null : e.target.value })}>
      <option value="text">Free text</option>
      <option value="number">Number</option>
      <option value="date">Date</option>
      <option value="datetime">Date &amp; time</option>
      <option value="choice">Choice (one of a list)</option>
      <option value="boolean">Yes / no</option>
      <option value="phone">Phone number</option>
      <option value="email">Email address</option>
    </select></Row>
    {d.dataType === 'choice' && <>
    <Row label="Choices (comma-separated; the answer must be one of these)">
      <input className={inp} value={(d.options || []).join(', ')}
        onChange={(e) => onChange({ options: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })}
        placeholder="Sedan, Big Van (leave empty when options come from an action)" />
    </Row>
    {/* B2 dynamic choices: options sourced from an action's response (e.g. a live priced menu). */}
    <Row label="…or options from an action's response">
      <div className="space-y-1">
        {(() => { const dy = d.dynamicOptions || {}; const set = (k, v) => onChange({ dynamicOptions: (v === '' && Object.values({ ...dy, [k]: '' }).every((x) => !x)) ? null : { ...dy, [k]: v || null } });
          return (<>
          <input className={inp} value={dy.hook || ''} onChange={(e) => set('hook', e.target.value.trim())} placeholder="Action name (e.g. vehicle-menu)" />
          <input className={inp} value={dy.itemsPath || ''} onChange={(e) => set('itemsPath', e.target.value.trim())} placeholder="Path to the list in the response (blank = the response itself)" />
          <input className={inp} value={dy.valueKey || ''} onChange={(e) => set('valueKey', e.target.value.trim())} placeholder="Each item's value property (e.g. rideCode)" />
          <input className={inp} value={dy.labelTemplate || ''} onChange={(e) => set('labelTemplate', e.target.value)} placeholder="Menu line per item, e.g. {{rideName}} — ${{estimatedAmount}}" />
          <input className={inp} value={dy.emptyMessage || ''} onChange={(e) => set('emptyMessage', e.target.value)} placeholder="What to say when the action returns no options" />
          </>); })()}
      </div>
    </Row>
    </>}
    </>}
    <label className="flex items-center gap-2 mb-2 text-slate-700"><input type="checkbox" checked={!!d.required} onChange={(e) => onChange({ required: e.target.checked })} />Required</label>
    {d.attrKind === 'FILE'
      ? <div className="text-[11px] text-blue-700 rounded-md bg-blue-50 border border-blue-200 p-2 mb-2">📎 The customer is shown an upload button; this attribute is satisfied <b>only</b> by an actual file (never guessed from text). Point an <b>AI vision</b> or <b>AI read-document</b> action at it, then branch on the result.</div>
      : d.attrKind === 'COMPONENT'
      ? <div className="text-[11px] text-violet-700 rounded-md bg-violet-50 border border-violet-200 p-2 mb-2">🧩 Satisfied <b>only</b> by a component result (e.g. a Pay button / <b>&lt;PayNow&gt;</b> callback). The customer can’t satisfy it by typing (“yes I’ve paid” won’t set it), so it’s safe as a gate before completion.</div>
      : <><Row label="Extraction hint (for the AI)"><textarea className={inp} rows={2} value={d.extractionHint || ''} onChange={(e) => onChange({ extractionHint: e.target.value })} /></Row>
    <label className="flex items-center gap-2 mb-2 text-slate-700"><input type="checkbox" checked={!!d.derived} onChange={(e) => onChange({ derived: e.target.checked })} />AI-inferred (never asked)</label></>}
    {!d.required && <label className="flex items-center gap-2 mb-2 text-slate-700" title="Ask this optional attribute once; the customer can skip it. Off = only captured if the customer volunteers it."><input type="checkbox" checked={!!d.offerOptional} onChange={(e) => onChange({ offerOptional: e.target.checked })} />Offer once (skippable)</label>}
    <Row label="AI profile (reusable)"><RefSelect value={d.aiProfileRef} items={libs?.aiProfiles} onPick={(v) => onChange({ aiProfileRef: v })} /></Row>
    {/* Wording — how this attribute is asked/confirmed. All optional; blank falls back to the AI/engine default. */}
    <div className="mt-3 mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Wording (optional — AI default if blank)</div>
    <Row label="Ask prompt"><textarea className={inp} rows={2} value={p.prompt || ''} onChange={(e) => setPrompt('prompt', e.target.value)} placeholder="How to ask for this (blank = AI default)" /></Row>
    <Row label="Re-ask (after an invalid answer)"><textarea className={inp} rows={2} value={p.reprompt || ''} onChange={(e) => setPrompt('reprompt', e.target.value)} /></Row>
    <Row label="Help text"><input className={inp} value={p.helpText || ''} onChange={(e) => setPrompt('helpText', e.target.value)} /></Row>
    {!gated && <>
    <Row label="Confirmation prompt (asks the customer to confirm; supports {{key}})"><input className={inp} value={p.confirmationPrompt || ''} onChange={(e) => setPrompt('confirmationPrompt', e.target.value)} placeholder="e.g. Just to confirm, is it {{email}}?" /></Row>
    <Row label="Confidence threshold (0–1; below this the AI asks to confirm)"><input className={inp} type="number" min={0} max={1} step={0.05} value={p.confidenceThreshold ?? ''} onChange={(e) => setPrompt('confidenceThreshold', e.target.value === '' ? null : parseFloat(e.target.value))} placeholder="e.g. 0.8" /></Row>
    {/* B3 threshold confirm: an unusually large NUMBER gets one "just checking?" turn. */}
    {d.dataType === 'number' && <Row label="Double-check when the number is at least…"><input className={inp} type="number" value={p.confirmAbove ?? ''} onChange={(e) => setPrompt('confirmAbove', e.target.value === '' ? null : parseFloat(e.target.value))} placeholder="e.g. 15 — '40 passengers, just checking?'" /></Row>}
    {/* B3 grouping: fields sharing a label are asked together (put the combined question on the first one). */}
    <Row label="Ask together with (group label)"><input className={inp} value={d.askGroup || ''} onChange={(e) => onChange({ askGroup: e.target.value.trim() || null })} placeholder="e.g. party — same label on each field asked as one question" /></Row>
    <Row label="Examples (comma-separated)"><input className={inp} value={listVal(d.examples)} onChange={(e) => setList('examples', e.target.value)} placeholder="Jane Doe, John Smith" /></Row>
    <Row label="Aliases (other words customers use)"><input className={inp} value={listVal(d.aliases)} onChange={(e) => setList('aliases', e.target.value)} placeholder="e-mail, email id" /></Row>
    <Row label="Negative examples (what NOT to extract)"><input className={inp} value={listVal(d.negativeExamples)} onChange={(e) => setList('negativeExamples', e.target.value)} placeholder="User, Customer, N/A" /></Row>
    </>}
    <div className="mt-3 mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Validation</div>
    <Row label="Reusable policies">
      <PolicyRuleEditor policyRefs={d.policyRefs} inlineRules={d.inlineRules} policies={libs?.policies} branches={branches} onChange={onChange} />
    </Row>
    <Row label="Domain-table lookups (value must exist in a reference dataset)">
      <DomainRuleEditor inlineRules={d.inlineRules} tables={libs?.domainTables} branches={branches} onChange={(v) => onChange({ inlineRules: v })} />
    </Row>
    {/* Ask ordering — WHEN this attribute is asked. Both are visible edges in the canvas. */}
    <div className="mt-3 mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Only ask after…</div>
    <Row label="…these attributes are provided">
      <MultiRef items={(attrNodes || []).filter((a) => a.id !== `attr:${d.key}` && a.id !== node.id)} selected={attrDepIds || []} onChange={onSetAttrDeps} empty="No other attributes yet." />
    </Row>
    <Row label="…these actions succeed">
      <MultiRef items={gateHookNodes} selected={attrGateIds || []} onChange={onSetAttrGates} empty="No actions yet — add an Action first." />
    </Row>
  </div>);
  }
  if (d.kind === 'branch') {
    // BUG-25(hunt #5): a branch's activation is a LIST of conditions the engine AND-combines — edit them all,
    // not just the first. Never let the list go empty in the UI so there's always a row to fill.
    const conds = (d.conditions && d.conditions.length) ? d.conditions : [{ attribute: '', op: 'EQUALS', value: '' }];
    const setCond = (i, patch) => onChange({ conditions: conds.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
    const addCond = () => onChange({ conditions: [...conds, { attribute: '', op: 'EQUALS', value: '' }] });
    const removeCond = (i) => onChange({ conditions: conds.length > 1 ? conds.filter((_, j) => j !== i) : conds });
    return (<div>
    <Head onDelete={onDelete}>Branch — a conditional path</Head>
    <Row label="Name"><input className={inp} value={d.name || ''} onChange={(e) => onChange({ name: e.target.value })} /></Row>
    <div className="text-xs text-slate-500 mb-1">Activates when <b>all</b> of these are true:</div>
    <div className="space-y-1.5">
      {conds.map((c, i) => (
        <div key={i} className="grid gap-1.5 items-center" style={{ gridTemplateColumns: '1fr 1fr 1fr auto' }}>
          <select className={inp} value={c.attribute || ''} onChange={(e) => setCond(i, { attribute: e.target.value })}><option value="">attribute</option>{attrKeys.map((k) => <option key={k}>{k}</option>)}</select>
          <select className={inp} value={c.op || 'EQUALS'} onChange={(e) => setCond(i, { op: e.target.value })}>{COND_OPS.map((o) => <option key={o}>{o}</option>)}</select>
          <input className={inp} value={c.value ?? ''} onChange={(e) => setCond(i, { value: e.target.value })} placeholder="value" />
          <button type="button" onClick={() => removeCond(i)} disabled={conds.length === 1}
            className="text-slate-400 hover:text-rose-500 disabled:opacity-30 px-1" title="Remove condition">✕</button>
        </div>
      ))}
    </div>
    <button type="button" onClick={addCond} className="mt-1 text-[11px] font-medium text-purple-600 hover:text-purple-800">+ AND condition</button>
    {/* Explicit attribute→branch assignment (the dropdown alternative to dragging handles). */}
    <Row label="Attributes collected only on this branch">
      <MultiRef items={attrNodes} selected={branchAttrIds || []} onChange={onSetBranchAttrs}
        empty="Add an attribute first (+ Attribute), then select it here." />
    </Row>
    <div className="mt-1 rounded-md bg-purple-50 border border-purple-100 p-2 text-[11px] text-purple-700 leading-relaxed">
      Checked attributes are asked <b>only</b> when this branch's condition is met (e.g. flightNumber for an
      “Airport” branch). Tip: you can also drag from the branch's right handle onto an Attribute node.
    </div>
  </div>);
  }
  if (d.kind === 'hook') {
    const t = actionType(d);
    const opts = parseOpts(d.url);
    const when = parseWhen(d.url);
    const decAttr = decisionAttr(d);
    const reaAttr = reasonAttr(d);
    const listToStr = (a) => a.join(', ');
    // Switch action type: reset the fields that don't belong to the new type so the graph stays clean.
    const setType = (nt) => {
      const base = { codeCapabilityRef: '', capabilityRef: '', url: '', responseMapping: {} };
      if (nt === 'decision') onChange({ ...base, url: decideUrl([]), trigger: d.trigger === 'ON_COMPLETE' ? 'ON_ATTRIBUTES' : (d.trigger || 'ON_ATTRIBUTES') });
      else if (nt === 'vision') onChange({ ...base, url: 'ai://vision', trigger: d.trigger === 'ON_COMPLETE' ? 'ON_ATTRIBUTES' : (d.trigger || 'ON_ATTRIBUTES') });
      else if (nt === 'readdoc') onChange({ ...base, url: 'ai://readdoc', trigger: d.trigger === 'ON_COMPLETE' ? 'ON_ATTRIBUTES' : (d.trigger || 'ON_ATTRIBUTES') });
      else if (nt === 'approval') onChange({ ...base, url: approveUrl([], null), trigger: 'ON_COMPLETE' });
      else if (nt === 'handoff') onChange({ ...base, url: handoffUrl(null), trigger: 'ON_COMPLETE' });
      else if (nt === 'code' || nt === 'capability') onChange({ ...base });
      else onChange({ ...base, method: d.method || 'POST' });
    };
    return (<div>
      <Head onDelete={onDelete}>Action (hook)</Head>
      {warning && <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-800 text-[11px] px-2 py-1.5 mb-2">⚠ {warning}</div>}
      <Row label="Name"><input className={inp} value={d.name} onChange={(e) => onChange({ name: e.target.value })} /></Row>
      <Row label="Type"><select className={inp} value={t} onChange={(e) => setType(e.target.value)}>
        <option value="rest">REST / URL</option>
        <option value="code">Code capability</option>
        <option value="capability">HTTP capability (reusable)</option>
        <option value="decision">AI decision</option>
        <option value="vision">AI vision (read an image)</option>
        <option value="readdoc">AI read document (PDF/CSV)</option>
        <option value="approval">Human approval</option>
        <option value="handoff">Live handoff</option>
      </select></Row>
      <Row label="When"><select className={inp} value={d.trigger || 'ON_COMPLETE'} onChange={(e) => onChange({ trigger: e.target.value })}>{TRIGGERS.map((tr) => <option key={tr}>{tr}</option>)}</select></Row>
      {d.trigger === 'ON_ATTRIBUTES' && <Row label="Fire after (attributes)">
        <MultiRef items={(attrKeys || []).map((k) => ({ id: k, name: k }))}
          selected={d.triggerAttributes || []} onChange={(v) => onChange({ triggerAttributes: v })} empty="Add attributes to the flow first." />
      </Row>}
      <Row label="Runs after these actions (dependency)">
        <MultiRef items={(hookNames || []).filter((h) => h !== d.name).map((h) => ({ id: h, name: h }))}
          selected={d.dependsOnHooks || []} onChange={(v) => onChange({ dependsOnHooks: v })} empty="No other actions yet." />
      </Row>

      {t === 'code' && <>
        <Row label="Code capability (sandboxed function)"><RefSelect value={d.codeCapabilityRef} items={libs?.codeCapabilities} onPick={(v) => onChange({ codeCapabilityRef: v })} /></Row>
        <Row label="Output → attribute (attr=outputKey per line)"><textarea className={inp} rows={3} value={Object.entries(d.responseMapping || {}).map(([k, v]) => `${k}=${v}`).join('\n')} onChange={(e) => { const o = {}; e.target.value.split('\n').forEach((l) => { const i = l.indexOf('='); if (i > 0) o[l.slice(0, i).trim()] = l.slice(i + 1).trim(); }); onChange({ responseMapping: o }); }} placeholder={'quotedPrice=price\ntripOutcome=status'} /></Row>
      </>}

      {t === 'capability' && <>
        <Row label="HTTP capability (reusable)"><RefSelect value={d.capabilityRef} items={libs?.capabilities} onPick={(v) => onChange({ capabilityRef: v })} /></Row>
        <div className="text-[11px] text-slate-500 rounded-md bg-slate-50 border border-slate-200 p-2 mb-2">Endpoint, body, connection &amp; mapping come from the capability. Configure those in <b>Library → Capabilities</b>.</div>
      </>}

      {t === 'decision' && <>
        <div className="text-[11px] text-indigo-700 rounded-md bg-indigo-50 border border-indigo-200 p-2 mb-2">🧠 The AI reads your <b>policy</b> and the case, then picks exactly one option (with a reason). Branch on the decision attribute below.</div>
        <Row label="Policy / knowledge"><textarea className={inp} rows={4} value={d.bodyTemplate || ''} onChange={(e) => onChange({ bodyTemplate: e.target.value })} placeholder={'Approve if damaged/defective. Deny if normal wear or >30 days. Review if subjective.\n\nCase: {{amount}} — {{reason}} — {{itemCondition}}'} /></Row>
        <Row label="Options (comma-separated)"><input className={inp} value={listToStr(opts)} onChange={(e) => onChange({ url: decideUrl(e.target.value.split(/[|,]/).map((s) => s.trim()).filter(Boolean)) })} placeholder="approve, deny, review" /></Row>
        <Row label="Decision → attribute"><input className={inp} value={decAttr} onChange={(e) => onChange({ responseMapping: decReaMapping(e.target.value.trim(), reaAttr) })} placeholder="refundDecision" /></Row>
        <Row label="Reason → attribute (optional)"><input className={inp} value={reaAttr} onChange={(e) => onChange({ responseMapping: decReaMapping(decAttr, e.target.value.trim()) })} placeholder="refundReason" /></Row>
      </>}

      {t === 'vision' && <>
        <div className="text-[11px] text-indigo-700 rounded-md bg-indigo-50 border border-indigo-200 p-2 mb-2">🖼 The AI reads the <b>image(s) the customer shared</b> in the chat and answers your instruction. Store the answer in an attribute, then branch on it or feed it to an AI decision. Fires once a file has been shared.</div>
        <Row label="Instruction (what to read / extract)"><textarea className={inp} rows={3} value={d.bodyTemplate || ''} onChange={(e) => onChange({ bodyTemplate: e.target.value })} placeholder={'Look at the product photo. Is the item damaged? Reply: DAMAGED or OK, then a short reason.'} /></Row>
        <Row label="Answer → attribute"><input className={inp} value={Object.keys(d.responseMapping || {})[0] || ''} onChange={(e) => onChange({ responseMapping: visionMapping(e.target.value.trim()) })} placeholder="damageAssessment" /></Row>
      </>}

      {t === 'readdoc' && <>
        <div className="text-[11px] text-indigo-700 rounded-md bg-indigo-50 border border-indigo-200 p-2 mb-2">📄 The AI extracts text from the <b>PDF / Excel / CSV / text file the customer shared</b> and answers your instruction (e.g. read a receipt, invoice, or a spreadsheet). Store the answer in an attribute, then branch on it. (Photos are handled by <b>AI vision</b>.)</div>
        <Row label="Instruction (what to read / extract)"><textarea className={inp} rows={3} value={d.bodyTemplate || ''} onChange={(e) => onChange({ bodyTemplate: e.target.value })} placeholder={'From this receipt, extract the merchant, total amount, and date. Reply as: merchant | total | date.'} /></Row>
        <Row label="Answer → attribute"><input className={inp} value={Object.keys(d.responseMapping || {})[0] || ''} onChange={(e) => onChange({ responseMapping: visionMapping(e.target.value.trim()) })} placeholder="receiptDetails" /></Row>
      </>}

      {t === 'approval' && <>
        <div className="text-[11px] text-amber-800 rounded-md bg-amber-50 border border-amber-200 p-2 mb-2">🙋 Blocks the session for a human reviewer (Approvals inbox), then resumes with their decision. Pair with an AI decision + a “when” gate to escalate only edge cases.</div>
        <Row label="Options (comma-separated)"><input className={inp} value={listToStr(opts)} onChange={(e) => onChange({ url: approveUrl(e.target.value.split(/[|,]/).map((s) => s.trim()).filter(Boolean), when) })} placeholder="approve, deny" /></Row>
        <Row label="Only escalate when (attribute = value)">
          <div className="flex items-center gap-1">
            <input className={inp} value={when.attr} onChange={(e) => onChange({ url: approveUrl(opts, { attr: e.target.value, val: when.val }) })} placeholder="refundDecision" />
            <span className="text-slate-400">=</span>
            <input className={inp} value={when.val} onChange={(e) => onChange({ url: approveUrl(opts, { attr: when.attr, val: e.target.value }) })} placeholder="review" />
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">Leave blank to always require approval.</div>
        </Row>
        <Row label="Decision → attribute"><input className={inp} value={decAttr} onChange={(e) => onChange({ responseMapping: decReaMapping(e.target.value.trim(), reaAttr) })} placeholder="refundDecision" /></Row>
        <Row label="Reviewer note → attribute (optional)"><input className={inp} value={reaAttr} onChange={(e) => onChange({ responseMapping: decReaMapping(decAttr, e.target.value.trim()) })} placeholder="refundNote" /></Row>
        <Row label="Reviewer summary (shown in the inbox)"><textarea className={inp} rows={2} value={d.bodyTemplate || ''} onChange={(e) => onChange({ bodyTemplate: e.target.value })} placeholder="Refund {{amount}} — {{reason}}" /></Row>
      </>}

      {t === 'handoff' && <>
        <div className="text-[11px] text-teal-800 rounded-md bg-teal-50 border border-teal-200 p-2 mb-2">🎧 Hands the conversation to a <b>live human operator</b> (they chat with the customer in real time, then hand back to the bot). Pair with a “when” gate to hand off only some cases.</div>
        <Row label="Only hand off when (attribute = value)">
          <div className="flex items-center gap-1">
            <input className={inp} value={when.attr} onChange={(e) => onChange({ url: handoffUrl({ attr: e.target.value, val: when.val }) })} placeholder="wantsHuman" />
            <span className="text-slate-400">=</span>
            <input className={inp} value={when.val} onChange={(e) => onChange({ url: handoffUrl({ attr: when.attr, val: e.target.value }) })} placeholder="yes" />
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">Leave blank to always hand off.</div>
        </Row>
        <Row label="Summary for the operator queue"><textarea className={inp} rows={2} value={d.bodyTemplate || ''} onChange={(e) => onChange({ bodyTemplate: e.target.value })} placeholder="Refund {{amount}} — {{reason}}" /></Row>
      </>}

      {t === 'rest' && <>
        <Row label="Connection (reusable — base URL + auth)"><RefSelect value={d.connectionRef} items={libs?.connections} onPick={(v) => onChange({ connectionRef: v })} /></Row>
        <Row label="URL (or ai://op, inventory://table)"><input className={inp} value={d.url || ''} onChange={(e) => onChange({ url: e.target.value })} placeholder="https://api/…  ·  ai://sentiment" /></Row>
        <Row label="Body template"><textarea className={inp} rows={2} value={d.bodyTemplate || ''} onChange={(e) => onChange({ bodyTemplate: e.target.value })} placeholder='{"q":"{{key}}"}' /></Row>
        <Row label="Response map (key=json.path per line)"><textarea className={inp} rows={2} value={Object.entries(d.responseMapping || {}).map(([k, v]) => `${k}=${v}`).join('\n')} onChange={(e) => { const o = {}; e.target.value.split('\n').forEach((l) => { const i = l.indexOf('='); if (i > 0) o[l.slice(0, i).trim()] = l.slice(i + 1).trim(); }); onChange({ responseMapping: o }); }} /></Row>
        {/* RT-01: per-hook request headers. Prefer a reusable Connection for auth; use this for one-off
            headers (X-Api-Key, Content-Type). These now round-trip through graphToFlow. */}
        <Row label="Headers (key=value per line — e.g. X-Api-Key=…)"><textarea className={inp} rows={2} value={Object.entries(d.headers || {}).map(([k, v]) => `${k}=${v}`).join('\n')} onChange={(e) => { const o = {}; e.target.value.split('\n').forEach((l) => { const i = l.indexOf('='); if (i > 0) o[l.slice(0, i).trim()] = l.slice(i + 1).trim(); }); onChange({ headers: o }); }} placeholder={'Authorization=Bearer …\nX-Api-Key=…'} /></Row>
      </>}

      {(t === 'approval' || t === 'handoff')
        ? <Row label={t === 'handoff' ? 'Connecting message (optional)' : 'Holding message (optional)'}><input className={inp} value={d.messageTemplate || ''} onChange={(e) => onChange({ messageTemplate: e.target.value })} placeholder={t === 'handoff' ? "Connecting you to a specialist…" : "A specialist is reviewing this — we'll follow up shortly."} /></Row>
        : <Row label="Message (shown to the user)"><input className={inp} value={d.messageTemplate || ''} onChange={(e) => onChange({ messageTemplate: e.target.value })} placeholder="Your fare is {{fare}}." /></Row>}
      {(t === 'rest' || t === 'code' || t === 'capability') && <>
        <Row label="On failure, show (customer-facing)"><input className={inp} value={d.errorMessage || ''} onChange={(e) => onChange({ errorMessage: e.target.value })} placeholder="We couldn't price that route — try another date." /></Row>
        <Row label="Component (transformer)"><input className={inp} value={d.componentTemplate || ''} onChange={(e) => onChange({ componentTemplate: e.target.value })} placeholder='<Quote id="{{quoteId}}"/>' /></Row>
      </>}
    </div>);
  }
  return null;
}
function Head({ children, onDelete }) {
  return (<div className="flex items-center justify-between mb-2"><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{children}</div>
    {onDelete && <button className="text-rose-500 text-xs" onClick={onDelete}>Delete</button>}</div>);
}

export default function OpsFlowCanvas(props) {
  return <ReactFlowProvider><Canvas {...props} /></ReactFlowProvider>;
}
